import {
    useCallback,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    Grid,
    LinearProgress,
    Paper,
    Typography,
} from "@mui/material";

import {
    Gavel,
} from "@material-ui/icons";

import {
    fetchAvailableAuctionsAPI,
} from "../api/auction_api";

import {
    handleResponse,
} from "../util/integration";

import {
    usePolling,
} from "../util/polling";

import {
    PRIMARY_COLOR,
} from "../util/constants";

import Auction from "../entity/Auction";

import {
    POLLING,
} from "../util/configuration";


const LABEL = Object.freeze({
    title: "Auktionen",
});


const MESSAGE = Object.freeze({
    noAuctions:
        "Keine verfügbaren Auktionen",

    pollingError:
        "Auktionen konnten nicht aktualisiert werden.",

    newAuctions: (count) => (
        count === 1
            ? "Eine neue Auktion ist verfügbar."
            : `${count} neue Auktionen sind verfügbar.`
    ),
});


const CONFIG = Object.freeze({
    topSpacing: "50px",
    headerPadding: "25px",
    contentSpacing: 7,
    pageSpacing: 5,
    auctionColumns: 4,
    fullWidthColumns: 12,
});


const STYLE = Object.freeze({
    topSpacing: {
        height: CONFIG.topSpacing,
    },

    titleContainer: {
        display: "flex",
        alignItems: "center",
        gap: 1.5,
    },

    titleIcon: {
        color: "white",
        fontSize: 30,
    },
});


const GRID_PROPS = Object.freeze({
    page: {
        container: true,
        spacing: CONFIG.pageSpacing,
    },

    fullWidthItem: {
        item: true,
        xs: CONFIG.fullWidthColumns,
    },

    content: {
        container: true,
        spacing: CONFIG.contentSpacing,
    },

    auctionItem: {
        item: true,
        xs: CONFIG.fullWidthColumns,
        md: CONFIG.auctionColumns,
    },
});


const TYPOGRAPHY_PROPS = Object.freeze({
    title: {
        variant: "h6",
        component: "h1",
    },
});


const createColorStyles = (
    primaryColor,
) => Object.freeze({
    header: {
        p: CONFIG.headerPadding,
        backgroundColor: primaryColor,
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
    },
});


const normalizeAuctionIds = (
    response,
) => {
    if (!Array.isArray(response)) {
        return [];
    }

    return response.filter(
        (auctionId) => (
            auctionId !== null
            && auctionId !== undefined
        ),
    );
};


const getNewAuctionCount = ({
    auctionIds,
    previousSnapshot,
    userId,
}) => {
    if (
        previousSnapshot === null
        || !Object.is(
            previousSnapshot.userId,
            userId,
        )
    ) {
        return 0;
    }

    const previousAuctionIds =
        new Set(
            previousSnapshot.auctionIds,
        );

    const newAuctionIds =
        new Set(
            auctionIds.filter(
                (auctionId) => (
                    !previousAuctionIds.has(
                        auctionId,
                    )
                ),
            ),
        );

    return newAuctionIds.size;
};


const useAuctions = ({
    accessToken,
    userId,
    showMessage,
}) => {
    const [
        auctionList,
        setAuctionList,
    ] = useState([]);

    const [
        isLoading,
        setIsLoading,
    ] = useState(true);

    const [
        reloadVersion,
        setReloadVersion,
    ] = useState(0);

    const previousAuctionSnapshotRef =
        useRef(null);


    const pollingRestartKey =
        useMemo(
            () => ({
                accessToken,
                userId,
                reloadVersion,
            }),
            [
                accessToken,
                reloadVersion,
                userId,
            ],
        );


    const handleAuctionResponse =
        useCallback(
            (response) => {
                const auctionIds =
                    normalizeAuctionIds(
                        response,
                    );

                const previousSnapshot =
                    previousAuctionSnapshotRef
                        .current;

                const newAuctionCount =
                    getNewAuctionCount({
                        auctionIds,
                        previousSnapshot,
                        userId,
                    });

                previousAuctionSnapshotRef
                    .current = {
                        userId,
                        auctionIds,
                    };

                setAuctionList(
                    auctionIds,
                );

                setIsLoading(
                    false,
                );

                if (newAuctionCount > 0) {
                    showMessage(
                        MESSAGE.newAuctions(
                            newAuctionCount,
                        ),
                    );
                }
            },
            [
                showMessage,
                userId,
            ],
        );


    const pollAuctions =
        useCallback(
            ({
                isPollingActive,
            }) => (
                new Promise(
                    (resolve, reject) => {
                        const handleSuccess = (
                            response,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                handleAuctionResponse(
                                    response,
                                );

                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        };


                        const handleFailure = (
                            message,
                        ) => {
                            if (
                                !isPollingActive()
                            ) {
                                resolve();
                                return;
                            }

                            try {
                                setIsLoading(
                                    false,
                                );

                                showMessage(
                                    message,
                                );

                                resolve();
                            } catch (error) {
                                reject(error);
                            }
                        };


                        try {
                            handleResponse(
                                fetchAvailableAuctionsAPI(
                                    accessToken,
                                    userId,
                                ),
                                handleSuccess,
                                handleFailure,
                            );
                        } catch (error) {
                            reject(error);
                        }
                    },
                )
            ),
            [
                accessToken,
                handleAuctionResponse,
                showMessage,
                userId,
            ],
        );


    const handlePollingError =
        useCallback(
            () => {
                setIsLoading(
                    false,
                );

                showMessage(
                    MESSAGE.pollingError,
                );
            },
            [
                showMessage,
            ],
        );


    usePolling({
        poll: pollAuctions,
        enabled: true,
        immediate: true,
        speed: POLLING.MEDIUM,
        onError: handlePollingError,
        restartKey: pollingRestartKey,
    });


    const reloadAuctions =
        useCallback(
            () => {
                setIsLoading(
                    true,
                );

                setReloadVersion(
                    (currentVersion) => (
                        currentVersion + 1
                    ),
                );
            },
            [],
        );


    return {
        auctionList,
        hasAuctions:
            auctionList.length > 0,
        isLoading,
        reloadAuctions,
    };
};


export default function Auctions({
    appAPI,
    contextAPI,
}) {
    const accessToken =
        contextAPI.password;

    const userId =
        contextAPI.id;

    const showMessage =
        appAPI.showMessage;


    const {
        auctionList,
        hasAuctions,
        isLoading,
        reloadAuctions,
    } = useAuctions({
        accessToken,
        userId,
        showMessage,
    });


    const colorStyle =
        useMemo(
            () => (
                createColorStyles(
                    PRIMARY_COLOR,
                )
            ),
            [],
        );


    return (
        <Grid
            {...GRID_PROPS.page}
        >
            <Grid
                {...GRID_PROPS.fullWidthItem}
            >
                <Box
                    sx={STYLE.topSpacing}
                />
            </Grid>

            <Grid
                {...GRID_PROPS.fullWidthItem}
            >
                <Grid
                    {...GRID_PROPS.content}
                >
                    <Grid
                        {...GRID_PROPS.fullWidthItem}
                    >
                        <Paper
                            sx={colorStyle.header}
                        >
                            <Box
                                sx={STYLE.titleContainer}
                            >
                                <Gavel
                                    sx={STYLE.titleIcon}
                                />

                                <Typography
                                    {...TYPOGRAPHY_PROPS.title}
                                >
                                    {LABEL.title}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    {isLoading && (
                        <Grid
                            {...GRID_PROPS.fullWidthItem}
                        >
                            <LinearProgress />
                        </Grid>
                    )}

                    {!isLoading
                        && hasAuctions
                        && auctionList.map(
                            (auctionId) => (
                                <Grid
                                    {...GRID_PROPS.auctionItem}
                                    key={auctionId}
                                >
                                    <Auction
                                        auctionId={auctionId}
                                        appAPI={appAPI}
                                        contextAPI={contextAPI}
                                        reload={reloadAuctions}
                                    />
                                </Grid>
                            ),
                        )}

                    {!isLoading
                        && !hasAuctions
                        && (
                            <Grid
                                {...GRID_PROPS.auctionItem}
                            >
                                <Typography>
                                    {MESSAGE.noAuctions}
                                </Typography>
                            </Grid>
                        )}
                </Grid>
            </Grid>
        </Grid>
    );
}
